import axios from 'axios';
import { api } from './api';
import { anonymousApi } from './redemption';
import { loadRecords, patchRecord } from '../utils/anonymousRedemptions';

export type DownloadScene = 'monitoring' | 'success' | 'failed' | 'unknown';
export interface DownloadTicket {
  sessionId: string; attempt: number; url: string; ticket: string; filename: string;
  datasetCode: string; packageSize: number; expiresAt: number; retryUntil: string;
  streamingAllowed: boolean;
}
export interface DownloadStatus {
  status: 'issued' | 'failed' | 'completed'; attempt: number; errorCode: string | null;
  chargedCount: number; remainingDownloads: number | null; canRetry: boolean;
}
export interface DownloadState {
  code: string; name: string; scene: DownloadScene; open: boolean; startedAt: number;
  phase: 'preparing' | 'choose' | 'launch' | 'transferring' | 'native' | 'done';
  receivedBytes: number; totalBytes: number; advice?: string; canRetry: boolean;
  status?: DownloadStatus; ticket?: DownloadTicket;
}
interface SaveFileHandle { createWritable(): Promise<WritableStream<Uint8Array>>; }
interface SavePickerWindow extends Window {
  showSaveFilePicker?: (options: { suggestedName: string; types: Array<{ description: string; accept: Record<string, string[]> }> }) => Promise<SaveFileHandle>;
}
type Owner = { token?: string };
type Task = { owner: Owner; requestId: string; retryId?: string; controller?: AbortController; generation: number; nativeOnly: boolean };
const listeners = new Set<() => void>();
let state: DownloadState | null = null;
let task: Task | null = null;
let pollTimer: ReturnType<typeof setTimeout> | undefined;
let nativeTimer: ReturnType<typeof setTimeout> | undefined;
let generation = 0;

export function subscribeDownload(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; }
export function getDownloadState() { return state; }
export function resetDownload() {
  task?.controller?.abort(); clearTimeout(pollTimer); clearTimeout(nativeTimer);
  task = null; state = null; listeners.forEach(listener => listener());
}
function update(patch: Partial<DownloadState>) {
  if (!state) return;
  state = { ...state, ...patch };
  listeners.forEach(listener => listener());
}
function current(run: Task) { return task === run; }
function client(owner: Owner) { return owner.token ? anonymousApi : api; }
function prefix(owner: Owner) { return owner.token ? '/anonymous/downloads' : '/downloads'; }
function config(owner: Owner) { return owner.token ? { headers: { Authorization: 'Bearer anon:' + owner.token } } : {}; }
function canUseFileSystem() { return typeof (window as SavePickerWindow).showSaveFilePicker === 'function'; }

function adviceFor(code: string): string {
  if (/EXPIRED|Signature|Expired|TICKET_INVALID/.test(code)) return '';
  if (/AccessDenied|403|permission|NotAllowed|SecurityError/.test(code)) return '请重新获取链接；若无法保存，可选择「桌面」或「文档」后重试。';
  if (/QuotaExceeded|space/.test(code)) return '请清理磁盘空间，或选择空间充足的位置后重试。';
  if (/Abort|cancel/.test(code)) return '保存已取消，需要时可重新获取下载链接。';
  if (/NOT_READY|NoSuchKey|404|PACKAGE/.test(code)) return '数据包暂不可用，请稍后重试或联系本站客服。';
  return '请确认网络连接正常后重新获取链接；如使用 VPN 或代理，可暂时关闭后重试。';
}

function nativeDownload(url: string) {
  // 独立浏览上下文：COS 错误页不会把本站和状态弹窗导航走。
  const a = document.createElement('a');
  a.href = url; a.target = '_blank'; a.rel = 'noopener noreferrer'; a.style.display = 'none';
  document.body.appendChild(a); a.click(); a.remove();
}

async function report(outcome: 'saved' | 'failed' | 'unknown', run = task) {
  const ticket = state?.ticket;
  if (!run || !ticket || !current(run)) return;
  try {
    await client(run.owner).post(prefix(run.owner) + '/sessions/' + ticket.sessionId + '/outcome',
      { outcome, attempt: ticket.attempt }, config(run.owner));
  } catch { /* 回执失败不改变本地保存结果，更不能触发计次。 */ }
}

function applyStatus(status: DownloadStatus, run: Task) {
  if (!current(run) || !state) return;
  const previouslyCompleted = state.status?.status === 'completed';
  update({ status, canRetry: status.canRetry });
  if (status.status === 'completed' && !previouslyCompleted) {
    window.dispatchEvent(new CustomEvent('zx:download-settled', { detail: { code: state.code } }));
  }
  if (run.owner.token && status.remainingDownloads !== null) {
    for (const rec of Object.values(loadRecords())) {
      if (rec.token === run.owner.token) patchRecord(rec.code, { remainingDownloads: status.remainingDownloads });
    }
  }
  if (status.attempt === state.ticket?.attempt && status.status === 'failed' && state.scene !== 'success') {
    run.controller?.abort();
    update({ scene: 'failed', phase: 'done', advice: adviceFor(status.errorCode || '') });
  }
  // COS 的传输完成不等于原生下载器已保存，不能把 unknown 自动改成 success。
}

function schedulePoll(run: Task, delay = 1000) {
  clearTimeout(pollTimer);
  pollTimer = setTimeout(async () => {
    const ticket = state?.ticket;
    if (!current(run) || !ticket) return;
    try {
      const response = await client(run.owner).get<DownloadStatus>(prefix(run.owner) + '/sessions/' + ticket.sessionId, config(run.owner));
      applyStatus(response.data, run);
    } catch {
      if (current(run) && state?.phase === 'native') update({ scene: 'unknown' });
    }
    if (current(run) && state?.status?.status !== 'completed' && Date.now() < Date.parse(ticket.retryUntil)) {
      schedulePoll(run, document.hidden ? 60_000 : 10_000);
    }
  }, delay);
}

async function consumeFile(run: Task, ticket: DownloadTicket) {
  if (!current(run)) return;
  const picker = (window as SavePickerWindow).showSaveFilePicker;
  if (!picker || run.nativeOnly) {
    nativeDownload(ticket.url);
    update({ phase: 'native' });
    clearTimeout(nativeTimer);
    nativeTimer = setTimeout(() => {
      if (current(run) && state?.scene === 'monitoring' && state.phase === 'native') {
        update({ scene: 'unknown' }); void report('unknown', run);
      }
    }, 10_000);
    return;
  }
  let handle: SaveFileHandle;
  try {
    handle = await picker.call(window, { suggestedName: ticket.filename, types: [{ description: 'ZIP 数据包', accept: { 'application/zip': ['.zip'] } }] });
  } catch (error) {
    if (!current(run)) return;
    if ((error as Error).name === 'SecurityError') {
      // 网络签票较慢导致点击激活过期，让用户明确点击保存按钮重新激活。
      update({ phase: 'choose' }); return;
    }
    update({ scene: 'failed', phase: 'done', advice: adviceFor((error as Error).name) });
    void report('failed', run); return;
  }
  if (!current(run)) return;
  const controller = new AbortController();
  run.controller = controller;
  let writer: WritableStreamDefaultWriter<Uint8Array> | undefined;
  let responseStarted = false;
  let writing = false;
  let lastProgress = 0;
  try {
    writing = true;
    writer = (await handle.createWritable()).getWriter();
    writing = false;
    update({ phase: 'transferring' });
    const response = await fetch(ticket.url, { signal: controller.signal, credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer' });
    responseStarted = true;
    if (!response.ok) throw new Error('COS_HTTP_' + response.status);
    if (response.status !== 200 || !response.body) throw new Error('INCOMPLETE_RESPONSE');
    const declaredLength = response.headers.get('Content-Length');
    if (declaredLength !== null && Number(declaredLength) !== ticket.packageSize) throw new Error('PACKAGE_SIZE_MISMATCH');
    const reader = response.body.getReader();
    let received = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (!current(run)) { controller.abort(); await writer.abort(); return; }
      received += value.byteLength;
      if (received > ticket.packageSize) throw new Error('PACKAGE_SIZE_MISMATCH');
      writing = true;
      await writer.write(value);
      writing = false;
      if (Date.now() - lastProgress > 250) { update({ receivedBytes: received }); lastProgress = Date.now(); }
    }
    if (received !== ticket.packageSize) throw new Error('INCOMPLETE_RESPONSE');
    writing = true;
    await writer.close();
    writer = undefined;
    if (!current(run) || state?.scene === 'failed') return;
    update({ scene: 'success', phase: 'done', receivedBytes: received, advice: undefined });
    void report('saved', run); // 仅记录客户端保存状态，后台不据此扣次。
  } catch (error) {
    controller.abort();
    if (writer) { try { await writer.abort(); } catch { /* 清理已关闭的流。 */ } }
    if (!current(run) || state?.scene === 'failed') return;
    const err = error as Error;
    // fetch 在收到可读响应前的 TypeError 可能来自 CORS；不能断言传输失败。
    if (!responseStarted && !writing && err.name === 'TypeError') {
      run.nativeOnly = true;
      update({ scene: 'unknown', phase: 'done', advice: undefined });
      void report('unknown', run);
    } else {
      update({ scene: 'failed', phase: 'done', advice: adviceFor(err.name === 'Error' ? err.message : err.name) });
      void report('failed', run);
    }
  }
}

async function issue(run: Task, retry: boolean) {
  if (!state) return;
  try {
    const path = retry && state.ticket ? '/sessions/' + state.ticket.sessionId + '/retry' : '/' + encodeURIComponent(state.code) + '/ticket';
    const response = await client(run.owner).post<DownloadTicket>(prefix(run.owner) + path,
      { requestId: retry ? run.retryId : run.requestId }, config(run.owner));
    if (!current(run)) return;
    const ticket = response.data;
    if (!ticket.streamingAllowed) run.nativeOnly = true;
    update({ ticket, name: ticket.filename.replace(/\.zip$/i, ''), totalBytes: ticket.packageSize });
    schedulePoll(run);
    if (!navigator.userActivation?.isActive) {
      update({ phase: canUseFileSystem() && !run.nativeOnly ? 'choose' : 'launch' });
    } else {
      void consumeFile(run, ticket);
    }
  } catch (error) {
    if (!current(run)) return;
    const code = axios.isAxiosError(error) ? String(error.response?.data?.code || '') : '';
    const explicit = axios.isAxiosError(error) && !!error.response;
    const unavailable = /QUOTA_EXHAUSTED|ENTITLEMENT|NO_ENTITLEMENT|SESSION_EXPIRED|RETRY_LIMIT/.test(code);
    update({ scene: explicit ? 'failed' : 'unknown', phase: 'done', canRetry: !unavailable,
      advice: explicit ? String((error as { response?: { data?: { message?: string } } }).response?.data?.message || adviceFor(code)) : undefined });
  }
}

async function begin(code: string, owner: Owner): Promise<{ charged: boolean }> {
  if (state && task && (state.phase === 'transferring' || state.phase === 'choose' || state.phase === 'launch' || state.phase === 'preparing' ||
      (state.code === code && task.owner.token === owner.token && Date.now() - state.startedAt < 9 * 60_000))) {
    update({ open: true }); return { charged: false };
  }
  clearTimeout(pollTimer); clearTimeout(nativeTimer);
  task = { owner, requestId: crypto.randomUUID(), generation: ++generation, nativeOnly: !canUseFileSystem() };
  state = { code, name: code, scene: 'monitoring', open: true, startedAt: Date.now(), phase: 'preparing', receivedBytes: 0, totalBytes: 0, canRetry: true };
  update({});
  await issue(task, false);
  return { charged: false };
}

export function downloadDataset(code: string) { return begin(code, {}); }
export function downloadDatasetAnonymous(code: string, token: string) { return begin(code, { token }); }
export function closeDownloadDialog() { update({ open: false }); }
export function openDownloadDialog() { update({ open: true }); }
export function showUnconfirmedDownload() { if (state?.scene === 'monitoring') { update({ scene: 'unknown' }); void report('unknown'); } }
export function reportDownloadFailed() {
  task?.controller?.abort(); clearTimeout(nativeTimer);
  update({ scene: 'failed', phase: 'done', advice: undefined }); void report('failed');
}
export function chooseDownloadLocation() {
  if (task && state?.ticket && (state.phase === 'choose' || state.phase === 'launch')) {
    // 在等待系统选择器期间锁住入口，防止双击创建两个并行写入。
    update({ phase: 'preparing' }); void consumeFile(task, state.ticket);
  }
}
export async function retryDownload() {
  if (!task || !state?.canRetry) return;
  task.controller?.abort(); clearTimeout(nativeTimer);
  const previous = task;
  task = { ...previous, retryId: previous.retryId || crypto.randomUUID(), generation: ++generation, controller: undefined };
  update({ scene: 'monitoring', phase: 'preparing', startedAt: Date.now(), receivedBytes: 0, advice: undefined });
  await issue(task, !!state.ticket);
  // 成功获取链接后下一次重试使用新标识；请求丢失时复用标识避免重复创建尝试。
  if (state?.phase !== 'done' && task) task.retryId = undefined;
}
